import React, { useState, useEffect } from 'react';
import { DBName } from '../types';
import { getAllNames, addName, updateName, deleteName } from '../services/adminApi';

const NamesAdmin: React.FC = () => {
  const [names, setNames] = useState<DBName[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [editingName, setEditingName] = useState<DBName | null>(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    loadNames();
  }, []);

  const loadNames = async () => {
    try {
      setLoading(true);
      const data = await getAllNames();
      setNames(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const handleAddName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;

    try {
      await addName(newName.trim());
      setNewName('');
      loadNames();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'ajout');
    }
  };

  const handleEditName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingName || !editText.trim()) return;

    try {
      await updateName(editingName.id, editText.trim());
      setEditingName(null);
      setEditText('');
      loadNames();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la modification');
    }
  };

  const handleDeleteName = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce nom ? Cela supprimera aussi tous les messages spéciaux associés.')) return;

    try {
      await deleteName(id);
      loadNames();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    }
  };

  const startEdit = (name: DBName) => {
    setEditingName(name);
    setEditText(name.name);
  };

  const cancelEdit = () => {
    setEditingName(null);
    setEditText('');
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Gestion des Noms</h2>
        <span className="text-sm text-gray-500">{names.length} nom(s)</span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Formulaire d'ajout */}
      <form onSubmit={handleAddName} className="mb-6 p-4 bg-gray-50 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ajouter un nouveau nom
        </label>
        <div className="flex space-x-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Tapez le nom ici..."
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="submit"
            disabled={!newName.trim()}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Ajouter
          </button>
        </div>
      </form>

      {/* Liste des noms */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {names.map((name) => (
          <div key={name.id} className="border border-gray-200 rounded-lg p-4 bg-white">
            {editingName?.id === name.id ? (
              <form onSubmit={handleEditName} className="space-y-3">
                <input
                  type="text"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="flex-1 px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                  >
                    ✅ Sauvegarder
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="flex-1 px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                  >
                    ❌ Annuler
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    👤 {name.name}
                  </h3>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => startEdit(name)}
                    className="flex-1 px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                  >
                    ✏️ Modifier
                  </button>
                  <button
                    onClick={() => handleDeleteName(name.id)}
                    className="flex-1 px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                  >
                    🗑️ Supprimer
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  ID: {name.id} • Créé: {new Date(name.created_at).toLocaleString('fr-FR')}
                </p>
              </>
            )}
          </div>
        ))}
      </div>

      {names.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          Aucun nom trouvé. Ajoutez-en un pour commencer !
        </div>
      )}
    </div>
  );
};

export default NamesAdmin;
